import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  AlertTriangle,
  Maximize2,
  Clock,
  ChevronDown,
  X,
  Calendar,
  ArrowRight,
} from "lucide-react";
import "./IncidentTimeline.scss";

// Time range options
const TIME_RANGE_OPTIONS = [
  { id: "1h", label: "Last 1 Hour", duration: 60 * 60 * 1000 },
  { id: "4h", label: "Last 4 Hours", duration: 4 * 60 * 60 * 1000 },
  { id: "1d", label: "Last 1 Day", duration: 24 * 60 * 60 * 1000 },
  { id: "nh", label: "Last N Hours", type: "input", unit: "hours" },
  { id: "nd", label: "Last N Days", type: "input", unit: "days" },
  { id: "nw", label: "Last N Weeks", type: "input", unit: "weeks" },
  { id: "custom", label: "Custom...", type: "custom" },
];

// Top count options
const TOP_COUNT_OPTIONS = [5, 10, 20, 30, 40, 50];

// Helper function to get severity color
function getSeverityColor(severity) {
  switch (severity?.toLowerCase()) {
    case "critical":
      return "#ef4444";
    case "high":
      return "#84cc16";
    case "medium":
      return "#eab308";
    case "low":
      return "#3b82f6";
    default:
      return "#64748b";
  }
}

// Helper function to generate incident ID from data
function generateIncidentId(incident) {
  const severity = incident.severity || "MEDIUM";
  const severityCode = severity.toUpperCase().substring(0, 4);
  const incidentNum = String(incident.incidentNumber || "0").padStart(5, "0");
  return `ACT-2025-${severityCode}-${incidentNum}`;
}

// Generate action items from incidents
function generateActionItems(incidents) {
  const actionTemplates = {
    Critical: [
      "Quarantine the Affected System Immediately",
      "Escalate to Security Operations Center",
      "Initiate Incident Response Protocol",
    ],
    High: [
      "Isolate the Compromised Endpoint from the Network",
      "Block Malicious IP Addresses",
      "Review Access Logs for Affected Systems",
    ],
    Medium: [
      "Apply Recommended Containment to Secure the System",
      "Update Security Signatures",
      "Monitor for Additional Suspicious Activity",
    ],
    Low: [
      "Review and Update Security Policies",
      "Schedule Security Awareness Training",
      "Document Incident for Future Reference",
    ],
  };

  const actions = [];
  incidents.slice(0, 3).forEach((incident, index) => {
    const severity = incident.severity || "Medium";
    const templates = actionTemplates[severity] || actionTemplates.Medium;
    const template = templates[index % templates.length];

    actions.push({
      id: generateIncidentId(incident),
      severity: severity,
      title: template,
      incidentId: generateIncidentId(incident),
      incidentUrl: incident.incidentUrl,
    });
  });

  return actions;
}

// Format time for display
function formatTime(date) {
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// Format date for display
function formatDisplayDate(date) {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

// Format full datetime
function formatDateTime(date) {
  return `${formatDisplayDate(date)} ${formatTime(date)}`;
}

// Custom Tooltip Component
function BarTooltip({ bar, position }) {
  if (!bar) return null;

  const duration = bar.endTime.getTime() - bar.startTime.getTime();
  const durationMinutes = Math.round(duration / 60000);
  const durationText =
    durationMinutes < 60
      ? `${durationMinutes} min`
      : `${Math.round(durationMinutes / 60)} hr ${durationMinutes % 60} min`;

  return (
    <div
      className={`timeline-tooltip ${
        position.showBelow ? "timeline-tooltip--below" : ""
      }`}
      style={{ left: position.x, top: position.y }}
    >
      <div className="timeline-tooltip__header">
        <span
          className="timeline-tooltip__severity"
          style={{ backgroundColor: bar.color }}
        >
          {bar.severity}
        </span>
        <span className="timeline-tooltip__id">#{bar.incidentNumber}</span>
      </div>
      <div className="timeline-tooltip__title">{bar.title}</div>
      <div className="timeline-tooltip__time">
        <div className="timeline-tooltip__time-row">
          <span className="timeline-tooltip__time-label">Start:</span>
          <span>{formatDateTime(bar.startTime)}</span>
        </div>
        <div className="timeline-tooltip__time-row">
          <span className="timeline-tooltip__time-label">End:</span>
          <span>{formatDateTime(bar.endTime)}</span>
        </div>
        <div className="timeline-tooltip__time-row">
          <span className="timeline-tooltip__time-label">Duration:</span>
          <span>{durationText}</span>
        </div>
      </div>
    </div>
  );
}

// Timeline Chart Component (reusable for main view and modal)
function TimelineChart({ bars, timeLabels, calculateBarPosition, timelineRange }) {
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    x: 0,
    y: 0,
    showBelow: false,
  });
  const chartRef = useRef(null);

  const handleMouseEnter = (bar, event) => {
    const rect = chartRef.current?.getBoundingClientRect();
    if (rect) {
      const chartWidth = rect.width;
      const relativeX = event.clientX - rect.left;
      const relativeY = event.clientY - rect.top;

      // Tooltip is 280–350px wide and centered via translate(-50%).
      // Clamp X so the tooltip doesn't overflow left/right edges.
      const tooltipHalfWidth = 150; // slightly more than half of min-width (280/2)
      const clampedX = Math.max(
        tooltipHalfWidth,
        Math.min(relativeX, chartWidth - tooltipHalfWidth)
      );

      // If bar is in top 150px of chart, show tooltip below the bar
      const showBelow = relativeY < 150;
      setTooltipPosition({
        x: clampedX,
        y: showBelow ? relativeY + 45 : relativeY - 10,
        showBelow,
      });
    }
    setHoveredBar(bar);
  };

  const handleMouseLeave = () => {
    setHoveredBar(null);
    };

    return (
    <div className="timeline-chart" ref={chartRef}>
      {/* Tooltip */}
      {hoveredBar && <BarTooltip bar={hoveredBar} position={tooltipPosition} />}

      {/* Bars */}
      <div className="timeline-chart__bars">
        {bars.map((bar, index) => {
          // Use actual startTime and endTime for positioning
          const position = calculateBarPosition(bar.startTime, bar.endTime, timelineRange);

          return (
            <div key={bar.id + index} className="timeline-chart__row">
              <div className="timeline-chart__bar-container">
                <div
                  className="timeline-chart__bar"
                  style={{
                    left: position.left,
                    width: position.width,
                    backgroundColor: bar.color,
                  }}
                  onMouseEnter={(e) => handleMouseEnter(bar, e)}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Show +N days indicator if bar extends beyond 24 hours */}
                  {bar.extraDays > 0 && (
                    <span className="timeline-chart__bar-extra-days">
                      +{bar.extraDays} {bar.extraDays === 1 ? 'day' : 'days'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Time Axis */}
      <div className="timeline-chart__axis">
        {timeLabels.map((label, index) => (
          <div
            key={index}
            className="timeline-chart__axis-label"
            style={{ left: `${label.position}%` }}
          >
            {label.label}
          </div>
        ))}
      </div>

      {/* Grid Lines */}
      <div className="timeline-chart__grid">
        {timeLabels.map((label, index) => (
          <div
            key={index}
            className="timeline-chart__grid-line"
            style={{ left: `${label.position}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// Custom Time Period Modal - Improved UI
function CustomTimeModal({ isOpen, onClose, onApply }) {
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("23:59");

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];
      setStartDate(dateStr);
      setEndDate(dateStr);
      setStartTime("00:00");
      setEndTime("23:59");
    }
  }, [isOpen]);

  const handleApply = () => {
    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);
    if (start < end) {
      onApply({ start, end });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="timeline-modal-overlay" onClick={onClose}>
      <div
        className="timeline-custom-modal timeline-custom-modal--enhanced"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="timeline-custom-modal__header">
          <div className="timeline-custom-modal__header-icon">
            <Calendar size={20} />
          </div>
          <div>
            <h3>Custom Time Range</h3>
            <p>Select a specific date and time range</p>
          </div>
          <button className="timeline-custom-modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="timeline-custom-modal__body">
          <div className="timeline-custom-modal__range-selector">
            {/* Start Time */}
            <div className="timeline-custom-modal__datetime-card">
              <div className="timeline-custom-modal__datetime-label">
                <span className="timeline-custom-modal__datetime-badge">
                  FROM
                </span>
              </div>
              <div className="timeline-custom-modal__datetime-inputs">
                <div className="timeline-custom-modal__input-group">
                  <Calendar size={16} />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="timeline-custom-modal__input-group">
                  <Clock size={16} />
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="timeline-custom-modal__arrow">
              <ArrowRight size={24} />
            </div>

            {/* End Time */}
            <div className="timeline-custom-modal__datetime-card">
              <div className="timeline-custom-modal__datetime-label">
                <span className="timeline-custom-modal__datetime-badge timeline-custom-modal__datetime-badge--end">
                  TO
                </span>
              </div>
              <div className="timeline-custom-modal__datetime-inputs">
                <div className="timeline-custom-modal__input-group">
                  <Calendar size={16} />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="timeline-custom-modal__input-group">
                  <Clock size={16} />
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="timeline-custom-modal__footer">
          <button
            className="timeline-custom-modal__btn timeline-custom-modal__btn--secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="timeline-custom-modal__btn timeline-custom-modal__btn--primary"
            onClick={handleApply}
          >
            Apply Range
          </button>
        </div>
      </div>
    </div>
  );
}

// Time Range Dropdown Component (reusable)
function TimeRangeDropdown({
  selectedTimeRange,
  onSelect,
  onCustomClick,
  showDropdown,
  setShowDropdown,
  dropdownRef,
}) {
  const [showNInput, setShowNInput] = useState(false);
  const [nInputType, setNInputType] = useState(null);
  const [nValue, setNValue] = useState("");

  const handleTimeRangeSelect = (option) => {
    if (option.type === "input") {
      setNInputType(option);
      setShowNInput(true);
      setNValue("");
    } else if (option.type === "custom") {
      setShowDropdown(false);
      onCustomClick();
    } else {
      onSelect(option);
      setShowDropdown(false);
      setShowNInput(false);
    }
  };

  const handleNApply = () => {
    const parsedValue = parseInt(nValue);
    if (nInputType && parsedValue > 0) {
      let duration;
      switch (nInputType.unit) {
        case "hours":
          duration = parsedValue * 60 * 60 * 1000;
          break;
        case "days":
          duration = parsedValue * 24 * 60 * 60 * 1000;
          break;
        case "weeks":
          duration = parsedValue * 7 * 24 * 60 * 60 * 1000;
          break;
        default:
          duration = parsedValue * 60 * 60 * 1000;
      }

      onSelect({
        id: nInputType.id,
        label: `Last ${parsedValue} ${
          nInputType.unit.charAt(0).toUpperCase() + nInputType.unit.slice(1)
        }`,
        duration,
      });
      setShowDropdown(false);
      setShowNInput(false);
    }
  };

  return (
    <div className="incident-timeline__time-picker" ref={dropdownRef}>
      <button
        className="incident-timeline__control-btn"
        onClick={() => {
          setShowDropdown(!showDropdown);
          setShowNInput(false);
        }}
      >
        <Clock size={14} />
        {selectedTimeRange.label}
        <ChevronDown size={14} />
      </button>

      {showDropdown && (
        <div className="incident-timeline__dropdown incident-timeline__dropdown--wide">
          {showNInput && nInputType ? (
            <div className="incident-timeline__n-input">
              <div className="incident-timeline__n-input-header">
                <span>{nInputType.label}</span>
                <span>N =</span>
              </div>
              <div className="incident-timeline__n-input-row">
                <input
                  type="number"
                  min="1"
                  placeholder="Enter value"
                  value={nValue}
                  onChange={(e) => setNValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleNApply()}
                  autoFocus
                />
                <button
                  className="incident-timeline__n-input-apply"
                  onClick={handleNApply}
                >
                  Apply
                </button>
                <button
                  className="incident-timeline__n-input-cancel"
                  onClick={() => setShowNInput(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="incident-timeline__dropdown-list">
              {TIME_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  className={`incident-timeline__dropdown-item ${
                    selectedTimeRange.id === option.id ? "is-active" : ""
                  }`}
                  onClick={() => handleTimeRangeSelect(option)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Top Count Dropdown Component
function TopCountDropdown({
  topCount,
  onSelect,
  showDropdown,
  setShowDropdown,
  dropdownRef,
}) {
  return (
    <div className="incident-timeline__top-picker" ref={dropdownRef}>
      <button
        className="incident-timeline__control-btn"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        TOP {topCount}
        <ChevronDown size={14} />
      </button>

      {showDropdown && (
        <div className="incident-timeline__dropdown">
          <div className="incident-timeline__dropdown-list">
            {TOP_COUNT_OPTIONS.map((count) => (
              <button
                key={count}
                className={`incident-timeline__dropdown-item ${
                  topCount === count ? "is-active" : ""
                }`}
                onClick={() => {
                  onSelect(count);
                  setShowDropdown(false);
                }}
              >
                TOP {count}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Constants for time calculations
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

// Process incidents helper (no longer returns actions - actions are independent)
function processIncidents(incidents, timeRange, customTimeRange, topCount) {
  if (!incidents || incidents.length === 0) {
    return { bars: [], timeRange: null };
  }

  const now = new Date();
  let startFilter, endFilter;

  if (customTimeRange) {
    startFilter = customTimeRange.start;
    endFilter = customTimeRange.end;
  } else if (timeRange && timeRange.duration) {
    endFilter = now;
    startFilter = new Date(now.getTime() - timeRange.duration);
  }

  let filteredIncidents = incidents;
  if (startFilter && endFilter) {
    filteredIncidents = incidents.filter((incident) => {
      const raw = incident._raw || incident;
      const incidentTime = new Date(raw.firstActivityTime || raw.createdAt);
      return incidentTime >= startFilter && incidentTime <= endFilter;
    });
  }

  const topIncidents = filteredIncidents.slice(0, topCount);

  if (topIncidents.length === 0) {
    return { bars: [], timeRange: null };
  }

  // Calculate timeline range based on actual incident times
  let minTime = Infinity;
  let maxTime = -Infinity;

  const bars = topIncidents.map((incident) => {
    const raw = incident._raw || incident;
    const startTime = new Date(raw.firstActivityTime || raw.createdAt);
    let endTime = new Date(raw.lastActivityTime || raw.updatedAt);
    const severity = raw.severity || incident.severity || "Medium";

    // If end time is before or equal to start time, set end to current time (ongoing incident)
    if (endTime.getTime() <= startTime.getTime()) {
      endTime = new Date(); // Use current time for ongoing incidents
    }

    // Calculate total duration and extra days beyond 24 hours
    const totalDuration = endTime.getTime() - startTime.getTime();
    const extraDays = Math.floor(totalDuration / TWENTY_FOUR_HOURS_MS);

    // Track min/max times for timeline range
    minTime = Math.min(minTime, startTime.getTime());
    maxTime = Math.max(maxTime, endTime.getTime());

    return {
      id: generateIncidentId({ ...incident, ...raw }),
      startTime,
      endTime,
      severity,
      color: getSeverityColor(severity),
      title: raw.title || incident.title,
      incidentNumber: raw.incidentNumber || incident.incidentNumber,
      extraDays, // Number of extra days to show as "+N days"
    };
  });

  // Add padding to the timeline (30 minutes on each side)
  const padding = 30 * 60 * 1000;
  minTime -= padding;
  maxTime += padding;

  return {
    bars,
    timeRange: { min: minTime, max: maxTime },
  };
}

// Generate time labels helper - Adapts to time range duration
function generateTimeLabels(timeRange) {
  if (!timeRange) return [];

  const { min, max } = timeRange;
  const duration = max - min;
  const labels = [];

  // Choose interval based on duration
  let interval;
  if (duration <= 2 * 60 * 60 * 1000) {
    // <= 2 hours: 5-minute intervals
    interval = 5 * 60 * 1000;
  } else if (duration <= 6 * 60 * 60 * 1000) {
    // <= 6 hours: 15-minute intervals
    interval = 15 * 60 * 1000;
  } else if (duration <= 24 * 60 * 60 * 1000) {
    // <= 24 hours: 1-hour intervals
    interval = 60 * 60 * 1000;
  } else if (duration <= 7 * 24 * 60 * 60 * 1000) {
    // <= 7 days: 6-hour intervals
    interval = 6 * 60 * 60 * 1000;
  } else {
    // > 7 days: 24-hour intervals
    interval = 24 * 60 * 60 * 1000;
  }

  const startTime = Math.floor(min / interval) * interval;

  for (let time = startTime; time <= max; time += interval) {
    const date = new Date(time);
    const position = ((time - min) / duration) * 100;
    
    // Only add labels that are within bounds
    if (position >= 0 && position <= 100) {
      labels.push({
        time,
        label: formatTime(date),
        position,
      });
    }
  }

  return labels;
}

// Calculate bar position helper
function createBarPositionCalculator(timeRange) {
  return (startTime, endTime, range) => {
    const effectiveRange = range || timeRange;
    if (!effectiveRange) return { left: "0%", width: "0.5%" };

    const { min, max } = effectiveRange;
    const duration = max - min;

    const startMs = startTime.getTime();
    const endMs = endTime.getTime();

    // Calculate positions as percentages
    const leftPercent = ((startMs - min) / duration) * 100;
    const widthPercent = ((endMs - startMs) / duration) * 100;

    return {
      left: `${Math.max(0, leftPercent)}%`,
      width: `${Math.max(0.3, widthPercent)}%`, // Minimum 0.3% for visibility
    };
  };
}

// Expanded Timeline Modal
function ExpandedTimelineModal({
  isOpen,
  onClose,
  incidents,
  selectedTimeRange,
  customTimeRange,
  onTimeRangeChange,
  onCustomTimeRangeChange,
}) {
  const [modalTopCount, setModalTopCount] = useState(10);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showTopDropdown, setShowTopDropdown] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  const timeDropdownRef = useRef(null);
  const topDropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(event.target)
      ) {
        setShowTimeDropdown(false);
      }
      if (
        topDropdownRef.current &&
        !topDropdownRef.current.contains(event.target)
      ) {
        setShowTopDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const processedData = useMemo(() => {
    return processIncidents(
      incidents,
      selectedTimeRange,
      customTimeRange,
      modalTopCount
    );
  }, [incidents, selectedTimeRange, customTimeRange, modalTopCount]);

  const timeLabels = useMemo(() => {
    return generateTimeLabels(processedData.timeRange);
  }, [processedData.timeRange]);

  const calculateBarPosition = useMemo(() => {
    return createBarPositionCalculator(processedData.timeRange);
  }, [processedData.timeRange]);

  const handleCustomApply = (range) => {
    onCustomTimeRangeChange(range);
    onTimeRangeChange({
      id: "custom",
      label: `${formatDisplayDate(range.start)} - ${formatDisplayDate(
        range.end
      )}`,
      duration: null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="timeline-modal-overlay" onClick={onClose}>
      <div
        className="timeline-expanded-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="timeline-expanded-modal__header">
          <h3>Activity Timeline - Expanded View</h3>
          <div className="timeline-expanded-modal__filters">
            {/* Time Range Dropdown */}
            <TimeRangeDropdown
              selectedTimeRange={selectedTimeRange}
              onSelect={(range) => {
                onTimeRangeChange(range);
                onCustomTimeRangeChange(null);
              }}
              onCustomClick={() => setShowCustomModal(true)}
              showDropdown={showTimeDropdown}
              setShowDropdown={setShowTimeDropdown}
              dropdownRef={timeDropdownRef}
            />

            {/* Top Count Dropdown */}
            <TopCountDropdown
              topCount={modalTopCount}
              onSelect={setModalTopCount}
              showDropdown={showTopDropdown}
              setShowDropdown={setShowTopDropdown}
              dropdownRef={topDropdownRef}
            />
          </div>
          <button className="timeline-expanded-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="timeline-expanded-modal__legend">
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--critical"></span>
            <span>Critical</span>
          </div>
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--high"></span>
            <span>High</span>
          </div>
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--medium"></span>
            <span>Medium</span>
          </div>
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--low"></span>
            <span>Low</span>
          </div>
        </div>
        <div className="timeline-expanded-modal__body">
          {processedData.bars.length > 0 ? (
            <TimelineChart
              bars={processedData.bars}
              timeLabels={timeLabels}
              calculateBarPosition={calculateBarPosition}
              timelineRange={processedData.timeRange}
            />
          ) : (
            <div className="incident-timeline__no-data">
              No timeline data available for the selected filters
            </div>
          )}
        </div>
      </div>

      {/* Custom Time Modal for Expanded View */}
      <CustomTimeModal
        isOpen={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        onApply={handleCustomApply}
      />
    </div>
  );
}

export default function IncidentTimeline({ incidents = [] }) {
  const [selectedTimeRange, setSelectedTimeRange] = useState(
    TIME_RANGE_OPTIONS[0]
  );
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customTimeRange, setCustomTimeRange] = useState(null);
  const [showExpandedModal, setShowExpandedModal] = useState(false);

  const timeDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(event.target)
      ) {
        setShowTimeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle custom time range
  const handleCustomApply = (range) => {
    setCustomTimeRange(range);
    setSelectedTimeRange({
      id: "custom",
      label: `${formatDisplayDate(range.start)} - ${formatDisplayDate(
        range.end
      )}`,
      duration: null,
    });
  };

  // Filter and process incidents for timeline (always TOP 5 for main view)
  const timelineData = useMemo(() => {
    return processIncidents(incidents, selectedTimeRange, customTimeRange, 5);
  }, [incidents, selectedTimeRange, customTimeRange]);

  // Generate actions independently from ALL incidents (not affected by filters)
  const actionItems = useMemo(() => {
    if (!incidents || incidents.length === 0) return [];
    // Sort by severity priority and take top 3
    const severityOrder = { Critical: 0, High: 1, Medium: 2, Low: 3 };
    const sortedIncidents = [...incidents].sort((a, b) => {
      const rawA = a._raw || a;
      const rawB = b._raw || b;
      const sevA = severityOrder[rawA.severity] ?? 4;
      const sevB = severityOrder[rawB.severity] ?? 4;
      return sevA - sevB;
    });
    return generateActionItems(
      sortedIncidents.slice(0, 3).map((i) => i._raw || i)
    );
  }, [incidents]);

  // Generate time axis labels
  const timeLabels = useMemo(() => {
    return generateTimeLabels(timelineData.timeRange);
  }, [timelineData.timeRange]);

  // Calculate bar positions
  const calculateBarPosition = useMemo(() => {
    return createBarPositionCalculator(timelineData.timeRange);
  }, [timelineData.timeRange]);

  return (
    <div className="incident-timeline">
      {/* Left Panel - Activity Timeline */}
      <div className="incident-timeline__chart-panel">
        <div className="incident-timeline__header">
          <div className="incident-timeline__title-section">
            <h3 className="incident-timeline__title">Activity Timeline</h3>
            <p className="incident-timeline__subtitle">
              This timeline helps track progress from detection to containment.
            </p>
          </div>
          <div className="incident-timeline__controls">
            {/* Time Range Picker */}
            <TimeRangeDropdown
              selectedTimeRange={selectedTimeRange}
              onSelect={(range) => {
                setSelectedTimeRange(range);
                setCustomTimeRange(null);
              }}
              onCustomClick={() => setShowCustomModal(true)}
              showDropdown={showTimeDropdown}
              setShowDropdown={setShowTimeDropdown}
              dropdownRef={timeDropdownRef}
            />

            {/* TOP 5 Label (no dropdown) */}
            <div className="incident-timeline__top-label">TOP 5</div>

            {/* Expand Button */}
            <button
              className="incident-timeline__expand-btn"
              onClick={() => setShowExpandedModal(true)}
              title="Expand Timeline"
            >
              <Maximize2 size={18} />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="incident-timeline__legend">
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--critical"></span>
            <span>Critical</span>
          </div>
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--high"></span>
            <span>High</span>
          </div>
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--medium"></span>
            <span>Medium</span>
          </div>
          <div className="incident-timeline__legend-item">
            <span className="incident-timeline__legend-dot incident-timeline__legend-dot--low"></span>
            <span>Low</span>
          </div>
        </div>

        {/* Custom Timeline Chart */}
        <div className="incident-timeline__chart-container">
          {timelineData.bars.length > 0 ? (
            <TimelineChart
              bars={timelineData.bars}
              timeLabels={timeLabels}
              calculateBarPosition={calculateBarPosition}
              timelineRange={timelineData.timeRange}
            />
          ) : (
            <div className="incident-timeline__no-data">
              No timeline data available for the selected time range
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Action Needed */}
      <div className="incident-timeline__action-panel">
        <div className="incident-timeline__action-header">
          <div className="incident-timeline__action-icon">
            <AlertTriangle size={20} color="#eab308" />
          </div>
          <div>
            <h3 className="incident-timeline__action-title">Action Needed</h3>
            <p className="incident-timeline__action-subtitle">
              Take immediate action to stop the threat and prevent further
              impact.
            </p>
          </div>
        </div>

        <div className="incident-timeline__action-list">
          {actionItems.length > 0 ? (
            actionItems.map((action, index) => (
              <div
                key={action.id + index}
                className="incident-timeline__action-item"
              >
                <div
                  className="incident-timeline__action-indicator"
                  style={{ backgroundColor: getSeverityColor(action.severity) }}
                ></div>
                <div className="incident-timeline__action-content">
                  <p className="incident-timeline__action-text">
                    {action.title}
                  </p>
                  <span className="incident-timeline__action-incident-id">
                    Incident Id: {action.incidentId}
                  </span>
                </div>
                <button
                  className="incident-timeline__action-link"
                  onClick={() =>
                    action.incidentUrl &&
                    window.open(action.incidentUrl, "_blank")
                  }
                  title="Open in Azure Portal"
                >
                  <Maximize2 size={14} />
                </button>
              </div>
            ))
          ) : (
            <div className="incident-timeline__no-actions">
              No actions required at this time
            </div>
          )}
        </div>
      </div>

      {/* Custom Time Modal */}
      <CustomTimeModal
        isOpen={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        onApply={handleCustomApply}
      />

      {/* Expanded Timeline Modal */}
      <ExpandedTimelineModal
        isOpen={showExpandedModal}
        onClose={() => setShowExpandedModal(false)}
        incidents={incidents}
        selectedTimeRange={selectedTimeRange}
        customTimeRange={customTimeRange}
        onTimeRangeChange={setSelectedTimeRange}
        onCustomTimeRangeChange={setCustomTimeRange}
      />
    </div>
  );
}
